// src/components/web/Footer/index.jsx
import React from "react";
import {
    Box,
    Container,
    Grid,
    Typography,
    IconButton,
    Divider,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import Icon from "@/theme/vectors/bundle";
import { ROUTES, CONTACT_INFO, SOCIAL_LINKS } from "@/utils/web/constants";
import { colors } from "@/theme/web-theme";

const Footer = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Handle scrolling to product showcase section
    const handleProductClick = (e) => {
        e.preventDefault();
        const productSection = document.getElementById("product-showcase");
        if (productSection) {
            productSection.scrollIntoView({ behavior: "smooth" });
        } else {
            // If not on homepage, navigate to homepage with hash
            window.location.href = ROUTES.HOME + "#product-showcase";
        }
    };

    // Handle scrolling to product details section
    const handleAboutClick = (e) => {
        e.preventDefault();
        const productDetailsSection = document.getElementById("product-details");
        if (productDetailsSection) {
            productDetailsSection.scrollIntoView({ behavior: "smooth" });
        } else {
            // If not on homepage, navigate to homepage with hash
            window.location.href = ROUTES.HOME + "#product-details";
        }
    };

    return (
        <Box sx={{ position: "relative", mt: 8 }}>
            <Box
                component="footer"
                sx={{
                    backgroundColor: colors.secondary.main,
                    py: 6,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    mt: "auto",
                    color: colors.secondary.contrastText,
                }}
            >
                <Container>
                    <Grid container spacing={4}>
                        {/* Description section (without logo) */}
                        <Grid item xs={12} md={4}>
                            <Stack
                                spacing={2}
                                alignItems={isMobile ? "center" : "flex-start"}
                                sx={{
                                    pt: 4,
                                    ml: isMobile ? 0 : "5%", // Match logo's left position
                                }}
                            >
                                <Box sx={{ position: "relative" }}>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "-130px",
                                            left: isMobile ? "50%" : "5%",
                                            transform: isMobile
                                                ? "translateX(-50%)"
                                                : "none",
                                            zIndex: 1100,
                                        }}
                                    >
                                        <Link to={ROUTES.HOME}>
                                            <Box
                                                component="img"
                                                src="assets/embox-logo.jpg"
                                                alt="Embox Logo"
                                                sx={{
                                                    height: 100,
                                                    width: "auto",
                                                    display: "block",
                                                    borderRadius: "25px",
                                                    boxShadow:
                                                        "0 4px 8px rgba(0,0,0,0.3)",
                                                    border: `3px solid ${colors.primary.main}`,
                                                }}
                                            />
                                        </Link>
                                    </Box>
                                </Box>

                                <Typography
                                    variant="body2"
                                    color={colors.secondary.contrastText}
                                    textAlign={isMobile ? "center" : "left"}
                                >
                                    {t("footer.description")}
                                </Typography>
                            </Stack>
                        </Grid>

                        {/* Quick Links */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                textAlign={isMobile ? "center" : "left"}
                                color={colors.primary.main}
                            >
                                {t("footer.quickLinks")}
                            </Typography>
                            <Stack
                                spacing={1}
                                alignItems={isMobile ? "center" : "flex-start"}
                            >
                                <Link
                                    to={ROUTES.HOME}
                                    style={{ textDecoration: "none" }}
                                >
                                    <Typography
                                        variant="body2"
                                        color={colors.secondary.contrastText}
                                        sx={{
                                            "&:hover": {
                                                color: colors.primary.main,
                                            },
                                            // textTransform: "uppercase",
                                        }}
                                    >
                                        {t("menu.home")}
                                    </Typography>
                                </Link>
                                <Box
                                    component="a"
                                    href="#"
                                    onClick={handleProductClick}
                                    style={{ textDecoration: "none" }}
                                >
                                    <Typography
                                        variant="body2"
                                        color={colors.secondary.contrastText}
                                        sx={{
                                            "&:hover": {
                                                color: colors.primary.main,
                                            },
                                            // textTransform: "uppercase",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {t("menu.products")}
                                    </Typography>
                                </Box>
                                <Box
                                    component="a"
                                    href="#"
                                    onClick={handleAboutClick}
                                    style={{ textDecoration: "none" }}
                                >
                                    <Typography
                                        variant="body2"
                                        color={colors.secondary.contrastText}
                                        sx={{
                                            "&:hover": {
                                                color: colors.primary.main,
                                            },
                                            // textTransform: "uppercase",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {t("menu.about")}
                                    </Typography>
                                </Box>
                            </Stack>
                            
                            {/* Social Media Links - Circular style */}
                            <Stack 
                                direction="row" 
                                spacing={2} 
                                sx={{ mt: 3 }}
                                justifyContent={isMobile ? "center" : "flex-start"}
                            >
                                <IconButton
                                    component="a"
                                    href={SOCIAL_LINKS.FACEBOOK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        backgroundColor: colors.primary.main,
                                        color: colors.primary.contrastText,
                                        width: 40,
                                        height: 40,
                                        '&:hover': {
                                            backgroundColor: colors.primary.dark,
                                        }
                                    }}
                                >
                                    <Icon name="facebook" size={20} />
                                </IconButton>
                                <IconButton
                                    component="a"
                                    href={SOCIAL_LINKS.TIKTOK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        backgroundColor: colors.primary.main,
                                        color: colors.primary.contrastText,
                                        width: 40,
                                        height: 40,
                                        '&:hover': {
                                            backgroundColor: colors.primary.dark,
                                        }
                                    }}
                                >
                                    <Icon name="tiktok" size={20} />
                                </IconButton>
                            </Stack>
                        </Grid>

                        {/* Contact Information */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                textAlign={isMobile ? "center" : "left"}
                                color={colors.primary.main}
                            >
                                {t("footer.contactInfo")}
                            </Typography>
                            <Stack
                                spacing={2}
                                alignItems={isMobile ? "center" : "flex-start"}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Icon
                                        name="phone"
                                        size={18}
                                        color={colors.primary.main}
                                    />
                                    <Typography
                                        variant="body2"
                                        color={colors.secondary.contrastText}
                                    >
                                        {t("footer.hotline")}:{" "}
                                        <a
                                            href={`tel:${CONTACT_INFO.HOTLINE.replace(/\s+/g, "")}`}
                                            style={{ color: 'inherit', textDecoration: 'none' }}
                                        >
                                            {CONTACT_INFO.HOTLINE}
                                        </a>
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                >
                                    <Icon
                                        name="shop"
                                        size={18}
                                        color={colors.primary.main}
                                        style={{ marginTop: 4 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color={colors.secondary.contrastText}
                                    >
                                        {t("footer.address")}:{" "}
                                        {CONTACT_INFO.ADDRESS}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 4, bgcolor: colors.secondary.light }} />

                    {/* Copyright */}
                    <Typography
                        variant="body2"
                        color={colors.secondary.contrastText}
                        align="center"
                    >
                        {new Date().getFullYear()} Embox.{" "}
                        {t("footer.copyright")}
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default Footer;
